import jsQR from "jsqr";

const SHORTENERS = new Set(["bit.ly","tinyurl.com","t.co","goo.gl","ow.ly","is.gd","buff.ly","rebrand.ly","cutt.ly","shorturl.at","rb.gy"]);
const URL_SCHEMES = /^(https?:\/\/)/i;

function classify(value) {
  if (URL_SCHEMES.test(value)) return "Website link";
  if (/^WIFI:/i.test(value)) return "Wi-Fi network";
  if (/^(BEGIN:VCARD|MECARD:)/i.test(value)) return "Contact card";
  if (/^mailto:/i.test(value)) return "Email address";
  if (/^tel:/i.test(value)) return "Phone number";
  if (/^(sms:|SMSTO:)/i.test(value)) return "Text message";
  if (/^(BEGIN:VEVENT|MATMSG:)/i.test(value)) return "Event or message";
  return "Text";
}

function inspect(value) {
  const notes = [];
  let level = "good";
  if (!URL_SCHEMES.test(value)) {
    notes.push("This is data, not a web link. Review it before copying or using it.");
    return { level, notes, isUrl: false };
  }
  let url;
  try { url = new URL(value); } catch { return { level: "danger", notes: ["The decoded text looks like a link but is not a valid URL."], isUrl: false }; }
  const host = url.hostname.toLowerCase();
  if (url.protocol !== "https:") { level = "caution"; notes.push("The destination uses HTTP, so the connection may not be encrypted."); }
  if (url.username || url.password) { level = "danger"; notes.push("The URL contains embedded login information, a pattern often used to disguise the real destination."); }
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(":")) { level = level === "danger" ? level : "caution"; notes.push("The destination uses a raw IP address instead of a recognizable domain."); }
  if (host.includes("xn--")) { level = level === "danger" ? level : "caution"; notes.push("The domain contains punycode. Confirm that international characters are not imitating another name."); }
  if (SHORTENERS.has(host.replace(/^www\./,""))) { level = level === "danger" ? level : "caution"; notes.push("This is a shortened link, so the final destination is hidden until it redirects."); }
  if (url.port && !["80","443"].includes(url.port)) { level = level === "danger" ? level : "caution"; notes.push("The link uses a non-standard network port."); }
  if (host.split(".").length > 5) { level = level === "danger" ? level : "caution"; notes.push("The destination has an unusually long chain of subdomains. Check the registered domain carefully."); }
  if (!notes.length) notes.push(`The visible destination is ${host}. Confirm that this is the domain you expected.`);
  notes.push("The scanner did not open the link automatically.");
  return { level, notes, isUrl: true, url: url.href };
}

async function imageData(file, canvas) {
  if (!file?.type?.startsWith("image/")) throw new Error("Choose an image file.");
  const bitmap = await createImageBitmap(file);
  const max = 2200;
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

function decode(data) {
  return jsQR(data.data, data.width, data.height, { inversionAttempts: "attemptBoth" })?.data || "";
}

function initSingle(root) {
  const canvas = root.querySelector("[data-decode-canvas]");
  const status = root.querySelector("[data-scan-status]");
  const result = root.querySelector("[data-scan-result]");
  const valueNode = root.querySelector("[data-result-value]");
  const typeNode = root.querySelector("[data-result-type]");
  const badge = root.querySelector("[data-risk-badge]");
  const list = root.querySelector("[data-risk-list]");
  const openLink = root.querySelector("[data-open-result]");
  const historyPanel = root.querySelector("[data-scan-history]");
  const historyList = root.querySelector("[data-history-list]");
  const historyLimit = 50;
  const historyKey = "qrscanonline.scan-history.v1";
  let current = "";
  let stream = null;
  let cameraFrame = null;
  const setStatus = (message, error = false) => { status.hidden = !message; status.textContent = message; status.classList.toggle("is-error", error); };
  const readHistory = () => { try { const items = JSON.parse(localStorage.getItem(historyKey) || "[]"); return Array.isArray(items) ? items.slice(0, historyLimit) : []; } catch { return []; } };
  const renderHistory = () => {
    const items = readHistory(); historyPanel.hidden = items.length === 0;
    historyList.replaceChildren(...items.map((item) => { const row = document.createElement("div"); const meta = document.createElement("span"); const text = document.createElement("button"); meta.textContent = item.type; text.type = "button"; text.textContent = item.value; text.title = "Show this result"; text.addEventListener("click", () => show(item.value, false)); row.append(meta, text); return row; }));
  };
  const remember = (text) => {
    try {
      const items = readHistory().filter((item) => item.value !== text); items.unshift({ value: text, type: classify(text), savedAt: Date.now() });
      localStorage.setItem(historyKey, JSON.stringify(items.slice(0, historyLimit))); renderHistory();
    } catch { /* Scanning still works when local storage is unavailable. */ }
  };
  const stopCamera = () => {
    cancelAnimationFrame(cameraFrame);
    stream?.getTracks().forEach((track) => track.stop()); stream = null;
    const video = root.querySelector("[data-camera-video]"); video.srcObject = null;
    root.querySelector("[data-camera-placeholder]").hidden = false; root.querySelector("[data-scan-frame]").hidden = true;
    root.querySelector("[data-camera-start]").hidden = false; root.querySelector("[data-camera-stop]").hidden = true;
  };
  const show = (text, save = true) => {
    current = text;
    const review = inspect(text);
    typeNode.textContent = classify(text); valueNode.textContent = text;
    badge.className = `risk-badge ${review.level === "caution" ? "risk-caution" : review.level === "danger" ? "risk-danger" : ""}`;
    badge.textContent = review.level === "danger" ? "High caution" : review.level === "caution" ? "Review" : "Decoded";
    list.replaceChildren(...review.notes.map((note) => { const li = document.createElement("li"); li.textContent = note; return li; }));
    if (review.isUrl) { openLink.href = review.url; openLink.hidden = false; } else { openLink.hidden = true; openLink.removeAttribute("href"); }
    result.hidden = false; if (save) remember(text); setStatus(""); stopCamera(); result.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const processFile = async (file) => {
    setStatus("Reading the QR code…"); result.hidden = true;
    try { const text = decode(await imageData(file, canvas)); if (!text) throw new Error("No readable QR code was found. Try a sharper image with the full code visible."); show(text); }
    catch (error) { setStatus(error.message || "The image could not be scanned.", true); }
  };
  root.querySelectorAll("[data-scan-tab]").forEach((tab) => tab.addEventListener("click", () => {
    root.querySelectorAll("[data-scan-tab]").forEach((item) => { const active = item === tab; item.classList.toggle("is-active", active); item.setAttribute("aria-selected", String(active)); });
    root.querySelectorAll("[data-scan-pane]").forEach((pane) => { pane.hidden = pane.dataset.scanPane !== tab.dataset.scanTab; });
    if (tab.dataset.scanTab !== "camera") stopCamera(); setStatus("");
  }));
  root.querySelector("[data-image-input]").addEventListener("change", (event) => processFile(event.target.files?.[0]));
  const drop = root.querySelector("[data-drop-zone]");
  ["dragenter","dragover"].forEach((name) => drop.addEventListener(name, (event) => { event.preventDefault(); drop.classList.add("is-dragging"); }));
  ["dragleave","drop"].forEach((name) => drop.addEventListener(name, (event) => { event.preventDefault(); drop.classList.remove("is-dragging"); }));
  drop.addEventListener("drop", (event) => processFile([...event.dataTransfer.files].find((file) => file.type.startsWith("image/"))));
  async function pasteImage() {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) { const type = item.types.find((candidate) => candidate.startsWith("image/")); if (type) return processFile(await item.getType(type)); }
      throw new Error("The clipboard does not contain an image.");
    } catch (error) { setStatus(error.message || "Clipboard access was not available. Try the keyboard paste shortcut.", true); }
  }
  root.querySelector("[data-paste-image]").addEventListener("click", pasteImage);
  document.addEventListener("paste", (event) => { if (!root.isConnected) return; const file = [...event.clipboardData.files].find((item) => item.type.startsWith("image/")); if (file) processFile(file); });
  root.querySelector("[data-camera-start]").addEventListener("click", async () => {
    const video = root.querySelector("[data-camera-video]"); const cameraCanvas = root.querySelector("[data-camera-canvas]");
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false }); video.srcObject = stream; await video.play();
      root.querySelector("[data-camera-placeholder]").hidden = true; root.querySelector("[data-scan-frame]").hidden = false; root.querySelector("[data-camera-start]").hidden = true; root.querySelector("[data-camera-stop]").hidden = false; setStatus("Point the camera at a QR code.");
      let last = 0;
      const scan = (time) => {
        if (!stream) return;
        if (time - last > 180 && video.readyState >= 2) { last = time; cameraCanvas.width = video.videoWidth; cameraCanvas.height = video.videoHeight; const context = cameraCanvas.getContext("2d", { willReadFrequently: true }); context.drawImage(video, 0, 0); const text = decode(context.getImageData(0, 0, cameraCanvas.width, cameraCanvas.height)); if (text) return show(text); }
        cameraFrame = requestAnimationFrame(scan);
      };
      cameraFrame = requestAnimationFrame(scan);
    } catch { setStatus("Camera access was unavailable. Allow permission or scan a saved image instead.", true); stopCamera(); }
  });
  root.querySelector("[data-camera-stop]").addEventListener("click", stopCamera);
  root.querySelector("[data-copy-result]").addEventListener("click", async () => { try { await navigator.clipboard.writeText(current); setStatus("Result copied to the clipboard."); } catch { setStatus("Copy was blocked. Select and copy the result manually.", true); } });
  root.querySelector("[data-scan-again]").addEventListener("click", () => { result.hidden = true; root.querySelector("[data-image-input]").value = ""; setStatus(""); drop.scrollIntoView({ behavior: "smooth", block: "center" }); });
  root.querySelector("[data-clear-history]").addEventListener("click", () => { try { localStorage.removeItem(historyKey); } catch {} renderHistory(); });
  window.addEventListener("pagehide", stopCamera);
  renderHistory();
}

function initBatch(root) {
  const input = root.querySelector("[data-batch-input]"); const canvas = root.querySelector("[data-batch-canvas]"); const progress = root.querySelector("[data-batch-progress]"); const results = root.querySelector("[data-batch-results]"); const body = root.querySelector("[data-batch-body]");
  let rows = [];
  input.addEventListener("change", async () => {
    const files = [...input.files].slice(0,100); rows = []; body.replaceChildren(); results.hidden = true; progress.hidden = false;
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index]; progress.textContent = `Scanning ${index + 1} of ${files.length}: ${file.name}`;
      let text = "", error = "";
      try { text = decode(await imageData(file, canvas)); if (!text) error = "No QR code found"; } catch (exception) { error = exception.message; }
      const review = text ? inspect(text) : { level: "", notes: [] }; const row = { file: file.name, type: text ? classify(text) : "—", result: text || error, review: review.level || "Failed" }; rows.push(row);
      const tr = document.createElement("tr"); [row.file,row.type,row.result,row.review].forEach((value) => { const td = document.createElement("td"); td.textContent = value; tr.appendChild(td); }); body.appendChild(tr);
    }
    progress.textContent = `${files.length} image${files.length === 1 ? "" : "s"} processed on this device.`; results.hidden = false;
  });
  root.querySelector("[data-export-csv]").addEventListener("click", () => {
    const quote = (value) => { let text = String(value); if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`; return `"${text.replaceAll('"','""')}"`; }; const csv = ["File,Type,Result,Review", ...rows.map((row) => [row.file,row.type,row.result,row.review].map(quote).join(","))].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" })); const link = document.createElement("a"); link.href = url; link.download = "qr-scan-results.csv"; link.click(); URL.revokeObjectURL(url);
  });
}

document.querySelectorAll("[data-scanner]").forEach(initSingle);
document.querySelectorAll("[data-batch-scanner]").forEach(initBatch);
